/**
 * <as-auto-controls>
 *
 * Turn-key parameter UI: renders a full two-step <as-step-selector> and auto-builds
 * the Edit step's controls from the configurator's params — the modern replacement
 * for Paramate's vendored `ControlsAuto` (paramateControlsAuto.js).
 *
 *   Edit    — one self-syncing as-components input per used/visible param, bucketed
 *             into <as-param-group> sections by each param's `style` group/group-title
 *             (the same grouping the old `grouping()` post-processor did, done here).
 *   Export  — <as-export-preview-panel> + <as-export-group>.
 *
 * Params are read from the <as-configurator> element in the page (via its getParams(),
 * which waits for init). The generated inputs bind via `trcad-param` and sync their
 * own value/min/max to Paramate — no per-configure rebuild needed.
 *
 * Built in light DOM (like <as-export-group>) so the step selector and inputs
 * participate in normal layout and register with the store. The host uses
 * `display: contents` so the inner <as-step-selector> sits directly in the parent.
 *
 * Localization
 *   Labels, tooltips and group headings all carry a derived dict key
 *   (`auto-parm.<param name>`), so a langFile can translate an auto-built UI
 *   without the trCAD script naming keys. Missing keys fall back to the param's
 *   own name. `group-title-dict-key` in a param's style overrides the group one;
 *   `attr.label-dict-key` / `attr.help-tooltip-dict-key` override a control's.
 *
 * Attributes
 *   edit-label    — Edit step label   (default "Edit")
 *   export-label  — Export step label (default "Export")
 *
 * Accessors
 *   .exportGroup  — the inner <as-export-group> (configure scopes/packageName)
 */

import '../text-and-lang/as-text.ts';
import './as-param-group.ts';
import '../step-selector/as-step-panel.ts';
import '../step-selector/step-selector.ts';
import '../inputs/numbox.ts';
import '../inputs/as-checkbox.ts';
import '../inputs/as-dropdown.ts';
import '../inputs/as-option.ts';
import '../inputs/as-string-input.ts';
import '../../features/importExport/import/as-file-input.ts';
import '../../features/importExport/export/as-export-group.ts';
import '../../features/importExport/export/as-export-preview-panel.ts';
import { devlog } from '../../util/utils.ts';
import { createElWithAttrs } from '../../util/wcUtil.ts';
import { params } from '../../store/paramStore.ts';
import type { ParamData } from '../../store/paramStore.ts';
import { whenConfigurator } from '../../util/singletons.ts';

type ParamStyle = Record<string, string>;

export class AsAutoControls extends HTMLElement {
  static readonly tag = 'as-auto-controls' as const;

  static get observedAttributes(): string[] {
    return ['edit-label', 'export-label'];
  }

  private _shellBuilt = false;
  private _unsubParams: (() => void) | null = null;
  private _editPanel: HTMLElement | null = null;
  private _exportGroupEl: HTMLElement | null = null;
  private _groups = new Map<string, HTMLElement>();
  private _controls = new Map<string, HTMLElement>();
  // Per-control sort key: `order` from the param's style (`order: N`), falling back to
  // source order via a monotonic creation sequence. Drives the order pass.
  private _seq = 0;
  private _orders = new WeakMap<HTMLElement, { order: number; seq: number }>();

  connectedCallback(): void {
    if (!this._shellBuilt) this._buildShell();
    this._initParams();
    // Re-sync the control set after every configure — params' used/visible state can
    // change, so different controls may need to appear or disappear.
    this._unsubParams ??= params.subscribeAll(table => this._syncControls(table));
  }

  disconnectedCallback(): void {
    this._unsubParams?.();
    this._unsubParams = null;
  }

  /** The inner <as-export-group>, for configuring scopes / package name. */
  get exportGroup(): HTMLElement | null {
    return this._exportGroupEl;
  }

  /** @deprecated Use `exportGroup`. */
  get exportStep(): HTMLElement | null {
    return this._exportGroupEl;
  }

  // ---------------------------------------------------------------------------
  // Shell
  // ---------------------------------------------------------------------------

  private _buildShell(): void {
    this._shellBuilt = true;
    // Transparent to layout: let the inner step selector act as a direct child.
    this.style.display = 'contents';

    const editLabel = this.getAttribute('edit-label') ?? 'Edit';
    const exportLabel = this.getAttribute('export-label') ?? 'Export';

    const frag = AsAutoControls._html(/*html*/ `
      <as-step-selector focus-on-change>
        <as-step-panel id="auto-edit-panel" label-default-text="${editLabel}"></as-step-panel>
        <as-step-panel id="auto-export-panel" label-default-text="${exportLabel}">
          <as-export-preview-panel></as-export-preview-panel>
          <as-export-group></as-export-group>
        </as-step-panel>
      </as-step-selector>
    `);

    this._editPanel = frag.querySelector('#auto-edit-panel');
    this._exportGroupEl = frag.querySelector('as-export-group');
    this.appendChild(frag);
  }

  // ---------------------------------------------------------------------------
  // Params
  // ---------------------------------------------------------------------------

  private _initParams(): void {
    // Resolves once <as-configurator> has connected and initialised. Later
    // updates come via the 'as-paramate-params-updated' listener.
    whenConfigurator()
      .then(() => this._syncControls(params.all()))
      .catch((err) => devlog('[as-auto-controls] waiting for the configurator failed:', err));
  }

  /**
   * Reconcile the rendered controls against the current params: add controls for
   * newly used/visible params, remove controls whose param is no longer used/visible,
   * and prune emptied groups. Existing controls are left in place so they keep their
   * focus and self-synced value/min/max — only the changed set is touched.
   */
  private _syncControls(params: Record<string, ParamData>): void {
    if (!this._editPanel || !params) return;

    const desired = new Set<string>();

    for (const key in params) {
      if (!Object.prototype.hasOwnProperty.call(params, key)) continue;
      const param = params[key];
      if (!param || !param.used) continue;

      const style = AsAutoControls._readStyleString(param.style);
      if (style.visible === 'false') continue;

      desired.add(key);

      // Already rendered — keep it (the input self-syncs value/min/max).
      if (this._controls.has(key)) continue;

      const control = this._createControl(key, param, style);
      if (!control) {
        desired.delete(key); // unsupported type — nothing to keep
        continue;
      }
      this._orders.set(control, this._orderInfo(style));
      this._containerFor(key, style).appendChild(control);
      this._controls.set(key, control);
    }

    // Remove controls whose param is no longer used/visible.
    for (const [key, el] of this._controls) {
      if (!desired.has(key)) {
        el.remove();
        this._controls.delete(key);
      }
    }

    this._pruneEmptyGroups();
    this._applyOrder();
  }

  /** Drop any <as-param-group> that no longer holds controls. */
  private _pruneEmptyGroups(): void {
    for (const [groupKey, group] of this._groups) {
      if (group.childElementCount === 0) {
        group.remove();
        this._groups.delete(groupKey);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Ordering
  // ---------------------------------------------------------------------------

  /** Sort key for a control: `order: N` from the style, then source/creation order. */
  private _orderInfo(style: ParamStyle): { order: number; seq: number } {
    const n = style.order != null && style.order !== '' ? parseFloat(style.order) : NaN;
    return { order: Number.isFinite(n) ? n : Number.POSITIVE_INFINITY, seq: this._seq++ };
  }

  /**
   * Order pass: sort controls within each group, then sort the groups within each
   * content panel — both keyed by `order` (style) then creation order. Only re-appends
   * when the current DOM order differs, so stable configures don't churn the DOM.
   */
  private _applyOrder(): void {
    for (const panel of this._contentPanels()) {
      for (const group of Array.from(panel.children) as HTMLElement[]) {
        this._orderChildren(group, (el) => {
          const o = this._orders.get(el);
          return o ? [o.order, o.seq] : [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];
        });
      }
      // A group sorts by its lowest-keyed control.
      this._orderChildren(panel, (group) => this._groupSortKey(group));
    }
  }

  /** Lowest [order, seq] among a group's controls — the group's own sort key. */
  private _groupSortKey(group: HTMLElement): [number, number] {
    let best: [number, number] = [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];
    for (const child of Array.from(group.children) as HTMLElement[]) {
      const o = this._orders.get(child);
      if (o && (o.order < best[0] || (o.order === best[0] && o.seq < best[1]))) {
        best = [o.order, o.seq];
      }
    }
    return best;
  }

  /** Re-append a parent's element children in sort-key order, skipping if unchanged. */
  private _orderChildren(parent: HTMLElement, keyOf: (el: HTMLElement) => [number, number]): void {
    const items = Array.from(parent.children) as HTMLElement[];
    if (items.length < 2) return;
    const sorted = items.slice().sort((a, b) => {
      const ka = keyOf(a);
      const kb = keyOf(b);
      return ka[0] - kb[0] || ka[1] - kb[1];
    });
    if (items.every((el, i) => el === sorted[i])) return; // already ordered
    for (const el of sorted) parent.appendChild(el);
  }

  /** Content panels controls can be placed into, indexed (panel 0 = Edit). */
  private _contentPanels(): HTMLElement[] {
    return this._editPanel ? [this._editPanel] : [];
  }

  /**
   * Resolve the <as-param-group> a control belongs in. Every control lives inside a
   * param group — params with an explicit `group` share a titled group; ungrouped
   * params share a single untitled default group per panel. Groups land in the panel
   * named by an explicit `panel:` style, defaulting to panel 0 (Edit). Created on
   * first sight, preserving source order. Group key is namespaced (e.g. `main:chamfer`)
   * to mirror the old grouping() behaviour.
   */
  private _containerFor(key: string, style: ParamStyle): HTMLElement {
    const panels = this._contentPanels();

    // Default to panel 0 (Edit); honour an explicit, in-range `panel:` style.
    let panelIndex = 0;
    if (style.panel != null && style.panel !== '') {
      const parsed = parseInt(style.panel, 10);
      if (Number.isInteger(parsed) && parsed >= 0 && parsed < panels.length) {
        panelIndex = parsed;
      } else {
        devlog(`[as-auto-controls] panel "${style.panel}" for ${key} out of range; using panel 0`);
      }
    }
    const panel = panels[panelIndex];

    const prefix = key.match(/.*:/)?.[0] ?? '';
    const groupKey = style.group ? prefix + style.group : `panel${panelIndex}:__default__`;

    let group = this._groups.get(groupKey);
    if (!group) {
      group = createElWithAttrs('as-param-group', {
        'subheading-default-text': style.group && (style['group-title'] || style.group),
        'subheading-dict-key': style.group
          && (style['group-title-dict-key'] || AsAutoControls._autoDictKey(groupKey, 'group.')),
      });
      this._groups.set(groupKey, group);
      panel.appendChild(group);
    }
    return group;
  }

  /** Map a param to the matching as-components input. Returns null for unsupported types. */
  private _createControl(key: string, param: ParamData, style: ParamStyle): HTMLElement | null {
    const label = param.name ?? key;
    const display = style.display;
    let el: HTMLElement;

    // A null/undefined attribute value is skipped, so the optional ones below
    // need no `if` — see createElWithAttrs in util/wcUtil.ts.
    const value = param.value != null ? String(param.value) : undefined;

    if ((display === 'dropdown' || display === 'radio') && Array.isArray(param.options)) {
      el = createElWithAttrs('as-dropdown', { 'label-default-text': label },
        param.options.map(opt =>
          createElWithAttrs('as-option', { value: String(opt.value) }, opt.text ?? String(opt.value))));
    } else {
      switch (param.type) {
        case 'bool':
          // On as-checkbox `value` is the form value string. The state is `checked`.
          el = createElWithAttrs('as-checkbox', {
            'label-default-text': label,
            checked: [true, 1, 'true', '1'].includes(param.value as any),
          });
          break;
        case 'int':
        case 'float':
          el = createElWithAttrs('as-numbox', {
            'label-default-text': label,
            value,
            step: style.step,
            unit: style.unit,
            'show-slider': display === 'range',
          });
          break;
        case 'string':
          el = createElWithAttrs('as-string-input', { 'label-default-text': label, value });
          break;
        case 'file':
          el = createElWithAttrs('as-file-input', { 'label-default-text': label });
          break;
        case 'array':
          // A file[] param is one upload taking several files at once. No other
          // element type has an input yet.
          if (param.elementType !== 'file') {
            devlog(`[as-auto-controls] Unsupported array of "${param.elementType}" for ${key}; skipping`);
            return null;
          }
          el = createElWithAttrs('as-file-input', { 'label-default-text': label, multiple: true });
          break;
        default:
          // color / vector / matrix / atrafo / ray — not yet supported.
          devlog(`[as-auto-controls] Unsupported param type "${param.type}" for ${key}; skipping`);
          return null;
      }
    }

    // Every control is param-linked and configures on change by default. trCAD can
    // override any attribute via the generic `attr.<name>: <value>` style convention,
    // e.g. `style = "attr.configure-on-change: false;"` to opt a param out.
    const dictKey = AsAutoControls._autoDictKey(key);

    el.setAttribute('trcad-param', key);
    el.setAttribute('label-dict-key', dictKey);
    if (param.descr) {
      el.setAttribute('help-tooltip-default-text', param.descr);
      el.setAttribute('help-tooltip-dict-key', `${dictKey}.descr`);
    }
    el.setAttribute('configure-on-change', '');
    this._applyStyleAttrs(el, style);

    return el;
  }

  /** Apply generic `attr.<name>: <value>` style directives onto a control. */
  private _applyStyleAttrs(el: HTMLElement, style: ParamStyle): void {
    for (const key in style) {
      if (!key.startsWith('attr.')) continue;
      const name = key.slice(5).trim();
      if (name) AsAutoControls._applyAttr(el, name, style[key]);
    }
  }

  /**
   * Set an attribute from a style value. A value of "false" removes the attribute
   * (so boolean attrs like configure-on-change can be turned off); "" or "true"
   * sets it present; anything else sets it verbatim.
   */
  private static _applyAttr(el: HTMLElement, name: string, raw: string): void {
    const v = (raw ?? '').trim();
    if (v.toLowerCase() === 'false') {
      el.removeAttribute(name);
    } else if (v === '' || v.toLowerCase() === 'true') {
      el.setAttribute(name, '');
    } else {
      el.setAttribute(name, v);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Dict key for a generated label: `auto-parm.<param name>`, with `.descr` added
   * for its tooltip and a `group.` segment for a group heading. A `main:` prefix is
   * dropped, any other package prefix becomes a segment (`auto-parm.chamfer.width`).
   * The key is only a fallback route: nothing has to translate it, because a key the
   * dictionary does not hold falls back to the default text.
   */
  private static _autoDictKey(key: string, kind = ''): string {
    return `auto-parm.${kind}${key.replace(/^main:/, '').replace(/:/g, '.')}`;
  }

  /** Parse a Paramate `style` string ("key: value; key: value") into an object. */
  private static _readStyleString(style?: string): ParamStyle {
    const out: ParamStyle = {};
    if (!style) return out;
    for (const part of style.split(';')) {
      const idx = part.indexOf(':');
      if (idx === -1) continue;
      const k = part.slice(0, idx).trim();
      if (!k) continue;
      out[k] = part.slice(idx + 1).trim();
    }
    return out;
  }

  private static _html(str: string): DocumentFragment {
    const tpl = document.createElement('template');
    tpl.innerHTML = str;
    return tpl.content;
  }
}

if (!customElements.get(AsAutoControls.tag)) {
  customElements.define(AsAutoControls.tag, AsAutoControls);
} else {
  console.warn('as-auto-controls already defined');
}
